import Typography from 'components/UI/Typography';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { FontSize, FontWeight } from 'modules/styles/variables';
import { MuiBox, MuiRadioGroup } from 'theme/material-ui';
import { createMuiTheme } from '@material-ui/core';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { UPDATE_CHECKLIST_PATIENT_INFO } from 'services/orchestration/mutations';
import { Formik, Field } from 'formik';
import CallOutlinedIcon from '@material-ui/icons/CallOutlined';
import { formatPhone } from 'services/formatPhone';
import { useHistory, useLocation } from 'react-router-dom';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import Config from 'react-native-config';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';

import {
  PersonalInfoInputLabel,
  PersonalInfoFormControl,
  PersonalInfoSection,
  MuiRow,
  MuiStacked,
  SectionHeader,
  ContactHeader,
  MuiMaskContainer,
  PhoneIcon,
  MaskedPreRegInput,
  PreRegInputBox,
  CheckBoxDiv
} from './styled';

import LanguageSelectionList from './LanguageSelection';
import ReligionSelectionList from './ReligionSelection';
import RaceSelection from './RaceSelection';
import EditMobilePhone from './EditMobile';
import EditEmail from './EditEmail';

import {
  PreRegistrationState,
  PatientInfo,
  PatientPhone,
  DemographicTypes,
  VisitDetail,
  CheckListItem,
  KeyValue,
  IProfileValues
} from 'store/preRegistration/types';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import {
  PreRegistrationPatientInfoSelector,
  PreRegistrationDemographicTypesSelector,
  PreRegistrationFetchingSelector,
  PreRegistrationCheckListStepByIdSelector,
  PreRegistrationVisitDetailSelector
} from 'store/preRegistration/selectors';
import { useMutation } from '@apollo/react-hooks';
import RequiredFieldsLegend, { StyledAsterisk } from 'components/RequiredFieldsLegend';
import { handleError, headerWithJWT } from '../utils';
import { errors } from '../constant';
import StyledButton from 'components/UI/Button/StyledButton';
import LinkButton from 'components/UI/Button/LinkButton';
import Spacer from 'components/UI/Layout/Spacer';
import { MaritalStatus } from './MaritalStatus';
import { DisplayEthnicity } from './Ethnicity';
import { HomeAddress } from './HomeAddress';
import { PreRegConfirmModal, PreRegHeader, PreRegInfoLabel } from '../common';
import { CheckListItemPageProps } from '../types';

import personalInfoModel from './FormConstant';
import { personalInfoValidationSchema } from './validationSchema';
import {
  UPDATE_PERSONAL_INFORMATION,
  UPDATE_PRE_REGISTRATION_CHECKLIST
} from 'shared/src/store/preRegistration/actions';
import { phoneNumberMask, cleanPhone } from 'shared/src/modules/utils/PhoneUtils';
import cloneDeep from 'lodash/cloneDeep';

const ALLOW_UPDATE = Config.PRE_REG_ALLOW_SAVE_PATIENT_DATA === 'enabled';
const ALLOW_ADDITIONAL_CONTACT = Config.PRE_REG_ALLOW_ADDITIONAL_CONTACT === 'enabled';
const ALLOW_TO_MARK_INCORRECT = Config.PRE_REG_ALLOW_TO_MARK_INCORRECT === 'enabled';

export const getDisplayName = (values?: KeyValue[], value?: string) => {
  return values
    ? values.find((item: KeyValue) => item.value === value)?.key || 'Not available'
    : 'Not available';
};

export function PersonalInformation(props: {
  patientInfo: PatientInfo;
  demographicTypes: DemographicTypes;
  step: CheckListItem;
  visitDetail: VisitDetail;
  isLoading: boolean;
  currentUrl: string;
  referringUrl: string;
}) {
  const {
    step,
    visitDetail,
    patientInfo,
    demographicTypes,
    currentUrl,
    referringUrl,
    isLoading
  } = props;

  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const [updateChecklistPatientInfo, { loading }] = useMutation(UPDATE_CHECKLIST_PATIENT_INFO);

  useEffect(() => {
    const { id } = location.state || {};
    if (!id || !step || !patientInfo.id) {
      history.replace('/pre-reg/checklist');
    }
  }, []);

  const initialValues: IProfileValues = {
    firstName: patientInfo?.firstName,
    lastName: patientInfo?.lastName,
    demographics: {
      maritalStatus: patientInfo?.demographics?.maritalStatus,
      races: patientInfo?.demographics?.races || [],
      ethnicity: patientInfo?.demographics?.ethnicity,
      religion: patientInfo?.demographics?.religion,
      primaryLanguage: patientInfo?.demographics?.primaryLanguage
    },
    emails: [
      {
        address: patientInfo?.emails?.length
          ? patientInfo?.emails.find(el => el.type === 'HOMEEMAIL')?.address
          : '',
        type: patientInfo?.emails?.length
          ? patientInfo?.emails.find(el => el.type === 'HOMEEMAIL')?.type
          : ''
      }
    ],
    phones: [
      {
        areaCode:
          patientInfo?.phones?.find((el: PatientPhone) => el.type === 'CELLPHONE')?.areaCode || '',
        phoneNumber:
          patientInfo?.phones?.find((el: PatientPhone) => el.type === 'CELLPHONE')?.phoneNumber ||
          '',
        type: 'CELLPHONE'
      },
      {
        areaCode:
          patientInfo?.phones?.find((el: PatientPhone) => el.type === 'HOMEPHONE')?.areaCode || '',
        phoneNumber:
          patientInfo?.phones?.find((el: PatientPhone) => el.type === 'HOMEPHONE')?.phoneNumber ||
          '',
        type: 'HOMEPHONE'
      },
      {
        areaCode:
          patientInfo?.phones?.find((el: PatientPhone) => el.type === 'OFFICEPHONE')?.areaCode ||
          '',
        phoneNumber:
          patientInfo?.phones?.find((el: PatientPhone) => el.type === 'OFFICEPHONE')?.phoneNumber ||
          '',
        type: 'OFFICEPHONE'
      },
      {
        areaCode:
          patientInfo?.phones?.find((el: PatientPhone) => el.type === 'PREFPHONE')?.areaCode || '',
        phoneNumber:
          patientInfo?.phones?.find((el: PatientPhone) => el.type === 'PREFPHONE')?.phoneNumber ||
          '',
        type: 'PREFPHONE'
      }
    ],
    addresses: [
      {
        address1: '',
        address2: '',
        city: '',
        state: '',
        type: '',
        zip: '',
        ...(patientInfo?.addresses?.find(address => address.type === 'HOMEADDR') || {})
      }
    ],
    addAdditionalContact: 'No',
    additionalCheckBoxApproval: false,
    additionalFirstName: '',
    additionalLastName: '',
    additionalRelationshipToPatient: '',
    additionalPhoneNumber: ''
  };

  const { formField } = personalInfoModel;
  const getChangedValues = (values: IProfileValues, initialValues: IProfileValues) => {
    return Object.entries(values).reduce((acc, [key, value]) => {
      const hasChanged = JSON.stringify(initialValues[key]) !== JSON.stringify(value);

      if (hasChanged) {
        acc[key] = value;
      }

      return acc;
    }, {});
  };

  const onConfirmButtonPress = async (value: IProfileValues) => {
    const changedValue = getChangedValues(value, initialValues);
    try {
      if (step.complete) {
        handleError(errors.Complete, history);
      } else {
        const fin = visitDetail.finNumber;
        const mrn = patientInfo.id;
        const mutationPayload = {
          arType: visitDetail.arType,
          visitType: visitDetail.type,
          checkListStepId: step.id,
          facilityCode: visitDetail.facilityCode,
          fin,
          mrn,
          stepData: { ...changedValue, cernerPersonId: patientInfo.cernerPersonId }
        };
        const res = await updateChecklistPatientInfo({
          variables: {
            payload: mutationPayload
          },
          context: {
            clientName: 'orchestration',
            headers: headerWithJWT()
          }
        });
        if (res.data.updateChecklistPatientInfoStep.success) {
          // Check to see that anything has been changed on the form
          const isUpdated = JSON.stringify(changedValue) !== '{}';
          if (isUpdated) {
            dispatch({
              type: UPDATE_PERSONAL_INFORMATION,
              payload: true
            });
          }
          dispatch({
            type: UPDATE_PRE_REGISTRATION_CHECKLIST,
            payload: res.data.updateChecklistPatientInfoStep.data
          });
          const detailTypeAddition = isUpdated ? ' - update my information' : '';
          const amplitudeEventData: AmplitudeEventData = {
            currentUrl,
            referringUrl,
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_location: visitDetail?.location?.name || '',
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_detail_type: `Hospital Registration: ${step.title}${detailTypeAddition}`
          };
          analyticsService.logEvent(AnalyticsEvent.HospitalFormDetailComplete, amplitudeEventData);
          history.push('/pre-reg/checklist');
        } else {
          handleError(errors[step.type as keyof typeof errors], history);
        }
      }
    } catch (err) {
      handleError(errors[step.type as keyof typeof errors], history);
    }
  };

  const formLabelsTheme = createMuiTheme({
    overrides: {
      MuiFormLabel: {
        asterisk: {
          color: '#db3131',
          '&$error': {
            color: '#db3131'
          }
        }
      }
    }
  });

  const unOrderedEthnicities = [...demographicTypes.ethnicity];
  const orderedEthnicities = unOrderedEthnicities.sort((a, b) => (a.key < b.key ? -1 : 1));

  return (
    <MuiBox flexDirection="column" alignContent="center" data-testid="personal-information">
      <SpinnerModal isLoading={isLoading || loading} />
      <PreRegHeader title="Personal Information" />

      <MuiBox m={1} p={1}>
        <Typography fontSize={FontSize.large} fontWeight={FontWeight.bold}>
          Please confirm that your personal information is correct
          {ALLOW_UPDATE ? ', and make any necessary edits' : ''}.
        </Typography>
      </MuiBox>

      {ALLOW_UPDATE ? (
        <MuiBox m={1} p={1}>
          <RequiredFieldsLegend />
        </MuiBox>
      ) : null}

      <MuiBox m={1} p={1}>
        <SectionHeader>Personal Information</SectionHeader>
        <MuiThemeProvider theme={formLabelsTheme}>
          <Formik
            initialValues={cloneDeep(initialValues)}
            validationSchema={personalInfoValidationSchema}
            onSubmit={onConfirmButtonPress}
          >
            {({ values, setFieldValue, handleSubmit, isValid }) => {
              const initialMobilePhoneValue = `${
                values.phones?.find(el => el.type === 'CELLPHONE')?.areaCode
              } ${values.phones?.find(el => el.type === 'CELLPHONE')?.phoneNumber}`;
              const initialHomePhoneValue = `${
                values.phones?.find(el => el.type === 'HOMEPHONE')?.areaCode
              } ${values.phones?.find(el => el.type === 'HOMEPHONE')?.phoneNumber}`;
              const initialWorkPhoneValue = `${
                values.phones?.find(el => el.type === 'OFFICEPHONE')?.areaCode
              } ${values.phones?.find(el => el.type === 'OFFICEPHONE')?.phoneNumber}`;
              const initialPrefPhoneValue = `${
                values.phones?.find(el => el.type === 'PREFPHONE')?.areaCode
              } ${values.phones?.find(el => el.type === 'PREFPHONE')?.phoneNumber}`;
              return (
                <form onSubmit={handleSubmit}>
                  <MuiStacked>
                    <PreRegInfoLabel label="Patient First Name" value={values.firstName} />

                    <PreRegInfoLabel label="Patient Last Name" value={values.lastName} />

                    <MaritalStatus
                      demographicTypes={demographicTypes}
                      value={values.demographics.maritalStatus}
                      handleMaritalStatusChange={value => {
                        setFieldValue('demographics.maritalStatus', value, true);
                      }}
                    />
                    <DisplayEthnicity
                      value={values.demographics.ethnicity}
                      orderedEthnicities={orderedEthnicities}
                      onChange={(event: Event) => {
                        setFieldValue('demographics.ethnicity', event.target?.value, true);
                      }}
                    />
                    <RaceSelection
                      handleRaceChange={value => {
                        setFieldValue('demographics.races', value, true);
                      }}
                      value={values.demographics.races}
                      raceList={demographicTypes.race}
                    />

                    <MuiRow>
                      <MuiBox>
                        <PersonalInfoInputLabel required={!!ALLOW_UPDATE}>
                          Religious Preference
                        </PersonalInfoInputLabel>
                        <PersonalInfoSection>
                          <PersonalInfoFormControl required={!!ALLOW_UPDATE}>
                            <PreRegInputBox
                              name={formField.religion.name}
                              disabled
                              value={getDisplayName(
                                demographicTypes.religion,
                                values.demographics.religion
                              )}
                            />
                          </PersonalInfoFormControl>
                          <ReligionSelectionList
                            handleReligionAccept={item => {
                              setFieldValue('demographics.religion', item.value, true);
                            }}
                            religionList={demographicTypes.religion}
                          />
                        </PersonalInfoSection>
                      </MuiBox>
                    </MuiRow>

                    <MuiRow>
                      <MuiBox>
                        <PersonalInfoInputLabel required={!!ALLOW_UPDATE}>
                          Preferred Language
                        </PersonalInfoInputLabel>
                        <PersonalInfoSection>
                          <PersonalInfoFormControl required={!!ALLOW_UPDATE}>
                            <PreRegInputBox
                              name={formField.language.name}
                              disabled
                              value={getDisplayName(
                                demographicTypes?.primaryLanguage,
                                values.demographics.primaryLanguage
                              )}
                            />
                          </PersonalInfoFormControl>
                          <LanguageSelectionList
                            handleLanguageAccept={item => {
                              setFieldValue('demographics.primaryLanguage', item.value, true);
                            }}
                            languageList={demographicTypes?.primaryLanguage}
                          />
                        </PersonalInfoSection>
                      </MuiBox>
                    </MuiRow>

                    <MuiBox>
                      <ContactHeader>Contact Information</ContactHeader>
                      <EditEmail
                        patientInfo={patientInfo}
                        initialEmail={values.emails[0].address}
                        setEmailAddress={item => {
                          setFieldValue('emails[0].address', item, true);
                        }}
                      />
                      <EditMobilePhone
                        patientInfo={patientInfo}
                        initialMobile={initialMobilePhoneValue}
                        setMobileNumber={value => {
                          const idx = values.phones.findIndex(el => el.type === 'CELLPHONE');
                          const phone = values.phones[idx];
                          const newValue = cleanPhone(value);
                          phone.areaCode = newValue.substring(0, 3);
                          phone.phoneNumber = newValue.substring(3, 10);
                          setFieldValue(`phones[${idx}]`, phone);
                        }}
                      />
                      <MuiBox>
                        <PersonalInfoInputLabel>Home Phone</PersonalInfoInputLabel>
                        <MuiMaskContainer>
                          <PersonalInfoFormControl>
                            <PhoneIcon>
                              <CallOutlinedIcon />
                            </PhoneIcon>
                            <MaskedPreRegInput
                              value={initialHomePhoneValue}
                              disabled={!ALLOW_UPDATE}
                              name={formField.homeNumber.name}
                              label={formField.homeNumber.label}
                              mask={phoneNumberMask}
                              onChange={(e: Event) => {
                                const idx = values.phones.findIndex(el => el.type === 'HOMEPHONE');
                                const phone = values.phones[idx];
                                const value = cleanPhone(e.target?.value);
                                phone.areaCode = value.substring(0, 3);
                                phone.phoneNumber = value.substring(3, 10);
                                setFieldValue(`phones[${idx}]`, phone);
                              }}
                            />
                          </PersonalInfoFormControl>
                        </MuiMaskContainer>
                      </MuiBox>
                      <MuiBox>
                        <PersonalInfoInputLabel>Work Phone</PersonalInfoInputLabel>
                        <MuiMaskContainer>
                          <PersonalInfoFormControl>
                            <PhoneIcon>
                              <CallOutlinedIcon />
                            </PhoneIcon>
                            <MaskedPreRegInput
                              value={initialWorkPhoneValue}
                              disabled={!ALLOW_UPDATE}
                              name={formField.workNumber.name}
                              label={formField.workNumber.label}
                              mask={phoneNumberMask}
                              onChange={(e: Event) => {
                                const idx = values.phones.findIndex(
                                  el => el.type === 'OFFICEPHONE'
                                );
                                const phone = values.phones[idx];
                                const value = cleanPhone(e.target?.value);
                                phone.areaCode = value.substring(0, 3);
                                phone.phoneNumber = value.substring(3, 10);
                                setFieldValue(`phones[${idx}]`, phone);
                              }}
                            />
                          </PersonalInfoFormControl>
                        </MuiMaskContainer>
                      </MuiBox>
                      {Config.PREFERRED_NUMBER_FEATURE === 'enabled' ? (
                        <MuiBox>
                          <PersonalInfoInputLabel required={!!ALLOW_UPDATE}>
                            Preferred Phone Number
                          </PersonalInfoInputLabel>
                          <MuiMaskContainer>
                            <PhoneIcon>
                              <CallOutlinedIcon />
                            </PhoneIcon>
                            <PersonalInfoFormControl>
                              <PreRegInputBox
                                value={initialPrefPhoneValue}
                                as={ALLOW_UPDATE ? 'select' : ''}
                                name={formField.preferredNumber.name}
                                label={formField.preferredNumber.label}
                                onChange={e => {
                                  const idx = values.phones.findIndex(
                                    el => el.type === 'PREFPHONE'
                                  );
                                  const phone = values.phones[idx];
                                  const value = cleanPhone(e.target.value);
                                  phone.areaCode = value.substring(0, 3);
                                  phone.phoneNumber = value.substring(3, 10);
                                  setFieldValue(`phones[${idx}]`, phone);
                                }}
                              >
                                {values.phones?.find((el: PatientPhone) => el.type === 'CELLPHONE')
                                  ?.phoneNumber ? (
                                  <option key="MBL" value={initialMobilePhoneValue}>
                                    {formatPhone(initialMobilePhoneValue)}
                                  </option>
                                ) : null}

                                {values.phones?.find((el: PatientPhone) => el.type === 'HOMEPHONE')
                                  ?.phoneNumber ? (
                                  <option key="HM" value={initialHomePhoneValue}>
                                    {formatPhone(initialHomePhoneValue)}
                                  </option>
                                ) : null}

                                {values.phones?.find(
                                  (el: PatientPhone) => el.type === 'OFFICEPHONE'
                                )?.phoneNumber ? (
                                  <option key="WRK" value={initialWorkPhoneValue}>
                                    {formatPhone(initialWorkPhoneValue)}
                                  </option>
                                ) : null}
                              </PreRegInputBox>
                            </PersonalInfoFormControl>
                          </MuiMaskContainer>
                        </MuiBox>
                      ) : null}
                      <HomeAddress
                        homeAddress={values.addresses[0]}
                        handleSubmit={value => {
                          delete value.country;
                          value.type = 'HOMEADDR';
                          setFieldValue('addresses[0]', value);
                        }}
                      />
                      {ALLOW_ADDITIONAL_CONTACT ? (
                        <MuiBox>
                          <Spacer spacing="large" />
                          <Typography>
                            Add Additional Contact for a person involved in your care and or payment
                            of care?
                            <StyledAsterisk />
                          </Typography>
                          <MuiRadioGroup
                            name={formField.additionalFirstName.name}
                            row
                            value={values.addAdditionalContact}
                            onChange={value => {
                              setFieldValue('addAdditionalContact', value, true);
                            }}
                          >
                            <div>
                              <Field type="radio" name="addAdditionalContact" value="Yes" />
                              Yes
                            </div>
                            <div>
                              <Field type="radio" name="addAdditionalContact" value="No" />
                              No
                            </div>
                          </MuiRadioGroup>
                          <MuiBox>
                            <Spacer size="medium" />
                            {values.addAdditionalContact === 'Yes' ? (
                              <MuiBox>
                                <MuiRow>
                                  <CheckBoxDiv>
                                    <Field type="checkbox" name="additionalCheckBoxApproval" />
                                  </CheckBoxDiv>
                                  <Typography>
                                    I hereby agree that by adding an Additional Contact for Care or
                                    Payment of Care, I give permission for Intermountain Healthcare
                                    to speak with this individual and discuss care or payment for my
                                    care. I also agree that Intermountain Healthcare will rely on
                                    this permission for calls about things like appointment
                                    scheduling and bill payment until I contact Intermountain
                                    Healthcare and instruct otherwise.
                                  </Typography>
                                </MuiRow>
                                <Spacer size="small" />
                                <PersonalInfoInputLabel>
                                  First Name
                                  <StyledAsterisk />
                                </PersonalInfoInputLabel>
                                <PersonalInfoFormControl>
                                  <PreRegInputBox
                                    name={formField.additionalFirstName.name}
                                    label={formField.additionalFirstName.label}
                                  />
                                </PersonalInfoFormControl>
                                <Spacer size="small" />
                                <PersonalInfoInputLabel>
                                  Last Name
                                  <StyledAsterisk />
                                </PersonalInfoInputLabel>
                                <PersonalInfoFormControl>
                                  <PreRegInputBox
                                    name={formField.additionalLastName.name}
                                    label={formField.additionalLastName.label}
                                  />
                                </PersonalInfoFormControl>
                                <Spacer size="small" />
                                <PersonalInfoInputLabel>
                                  Relationship to Patient
                                  <StyledAsterisk />
                                </PersonalInfoInputLabel>
                                <PersonalInfoFormControl>
                                  <PreRegInputBox
                                    name={formField.additionalRelationshipToPatient.name}
                                    label={formField.additionalRelationshipToPatient.label}
                                  />
                                </PersonalInfoFormControl>
                                <Spacer size="small" />
                                <PersonalInfoInputLabel>
                                  Preferred Telephone Number
                                </PersonalInfoInputLabel>
                                <MuiMaskContainer>
                                  <PersonalInfoFormControl>
                                    <PhoneIcon>
                                      <CallOutlinedIcon />
                                    </PhoneIcon>
                                    <MaskedPreRegInput
                                      name={formField.additionalContactPhoneNumber.name}
                                      label={formField.additionalContactPhoneNumber.label}
                                      mask={phoneNumberMask}
                                      onChange={e => {
                                        setFieldValue(
                                          'additionalPhoneNumber',
                                          e.target.value.replace(/\D/g, '')
                                        );
                                      }}
                                    />
                                  </PersonalInfoFormControl>
                                </MuiMaskContainer>
                              </MuiBox>
                            ) : null}
                          </MuiBox>
                        </MuiBox>
                      ) : null}
                    </MuiBox>
                    <Spacer spacing="large" />
                    <MuiBox>
                      <StyledButton
                        data-testid="pre-reg-personal-confirm"
                        title="Confirm"
                        onClick={() => onConfirmButtonPress(values)}
                        disabled={!isValid}
                      >
                        Confirm
                      </StyledButton>
                    </MuiBox>
                    <Spacer spacing="small" />
                    <MuiBox>
                      {ALLOW_TO_MARK_INCORRECT ? (
                        <LinkButton disabled={!isValid} onPress={() => setShowUpdateDialog(true)}>
                          This information is not correct
                        </LinkButton>
                      ) : null}

                      <PreRegConfirmModal
                        icon=""
                        isOpen={showUpdateDialog}
                        title="No worries. Please call 888-269-8674 or, when you arrive at the hospital, we will update your personal information."
                        onConfirm={() => onConfirmButtonPress(true)} // nothing is changing we just send the same stuff.
                        onCancel={() => setShowUpdateDialog(false)}
                        confirmLabel="Ok"
                        style={{ paddingTop: '32px' }}
                        backdropColor="rgba(0,0,0,0.4)"
                        cancelLabel=""
                      />
                    </MuiBox>
                  </MuiStacked>
                </form>
              );
            }}
          </Formik>
        </MuiThemeProvider>
      </MuiBox>
    </MuiBox>
  );
}

const mapStateToProps = (state: PreRegistrationState, props: CheckListItemPageProps) => {
  const { id } = props.location?.state || {};
  return {
    step: PreRegistrationCheckListStepByIdSelector(id)(state),
    visitDetail: PreRegistrationVisitDetailSelector(state),
    patientInfo: PreRegistrationPatientInfoSelector(state),
    demographicTypes: PreRegistrationDemographicTypesSelector(state),
    isLoading: PreRegistrationFetchingSelector(state),
    currentUrl: currentLocationPathNameSelector(state),
    referringUrl: previousLocationPathNameSelector(state)
  };
};

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(PersonalInformation);
